/**
 * shadcn/ui DropdownMenu — vendored + tokenized for CertAIn v2 (Phase 1 fan-out foundation).
 *
 * Radix DropdownMenu (Root / Trigger / Content / Item / …). Content popover uses
 * the elevated surface + pop shadow; items highlight on `surface-subtle`. No
 * lucide, no raw color — resolves inside `[data-theme="v2"]`. Guard-clean.
 *
 * Used by the `…` row-action column on the All Candidates / Jobs tables, sort
 * menus in the toolbar, and any Settings overflow menu.
 */
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cn } from "@/lib/utils";
import { usePortalHost } from "@/components/v2/layout/PortalHostContext";

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  // Mount inside the zoomed shell host so the menu matches app scale under the
  // app-scale dial; `?? undefined` falls back to document.body off the v2 shell.
  const container = usePortalHost() ?? undefined;
  return (
  <DropdownMenuPrimitive.Portal container={container}>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // [zoom:var(--app-scale)] scales the menu to match the app-scale dial.
        // The host is at body-zoom-1 (correct fixed positioning); the content
        // zooms in place from its anchored top-left. See PortalHostContext.
        "[zoom:var(--app-scale)]",
        "z-50 min-w-[9rem] overflow-hidden rounded-[var(--radius,0.5rem)] border border-border-default bg-surface-elevated p-1 text-text-primary shadow-[var(--shadow-pop)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-[calc(var(--radius,0.5rem)-3px)] px-2 py-1.5 text-[13px] text-text-primary outline-none transition-colors",
      "focus:bg-surface-subtle data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "[&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-border-default", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName =
  DropdownMenuPrimitive.Separator.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
